import { useState, useCallback } from 'react';
import AzureAIService from '../../../../../services/azureAIService';
import WizardSubmissionService from '../../../../../services/wizardSubmissionService';
import { normalizeCvData } from '../utils/normalizers';

/**
 * Custom hook for managing AI Analytics and translations
 */
export const useAnalytics = (submission) => {
    const [aiAnalytics, setAiAnalytics] = useState(null);
    const [loadingAdvancedAnalytics, setLoadingAdvancedAnalytics] = useState(false);
    const [generatingAnalytics, setGeneratingAnalytics] = useState(false);

    // Language states
    const [analyticsLanguage, setAnalyticsLanguage] = useState('english');
    const [tamilAnalytics, setTamilAnalytics] = useState(null);
    const [translatingAnalytics, setTranslatingAnalytics] = useState(false);

    // Sales analytics data
    const [salesMetrics, setSalesMetrics] = useState(null);
    const [competitorAnalysis, setCompetitorAnalysis] = useState(null);
    const [marketInsights, setMarketInsights] = useState(null);
    const [followUpTasks, setFollowUpTasks] = useState([]);
    const [riskFactors, setRiskFactors] = useState([]);
    const [opportunityScore, setOpportunityScore] = useState(0);

    // Load existing Tamil analytics from submission
    const loadTamilAnalytics = useCallback((submissionData) => {
        if (submissionData.cv_data?.tamilAnalytics) {
            console.log('🔍 Found Tamil analytics in database');
            setTamilAnalytics(submissionData.cv_data.tamilAnalytics);
        }
    }, []);

    // Get current analytics based on language
    const getCurrentAnalytics = useCallback(() => {
        console.log('🔍 Getting current analytics:', {
            analyticsLanguage,
            hasTamilAnalytics: !!tamilAnalytics,
            hasAiAnalytics: !!aiAnalytics
        });

        return analyticsLanguage === 'tamil' && tamilAnalytics ? tamilAnalytics : aiAnalytics;
    }, [analyticsLanguage, tamilAnalytics, aiAnalytics]);

    // Translate analytics to Tamil
    const translateToTamil = useCallback(async (submissionData, forceRetranslate = false) => {
        try {
            setTranslatingAnalytics(true);
            console.log('🌐 Translating analytics to Tamil...');

            if (!aiAnalytics) {
                throw new Error('No analytics to translate');
            }

            // Check if translation already exists
            if (tamilAnalytics && !forceRetranslate) {
                console.log('ℹ️ Tamil translation already exists');
                setAnalyticsLanguage('tamil');
                return tamilAnalytics;
            }

            const azureAI = new AzureAIService();

            const translationPromise = azureAI.translateAnalyticsToTamil(aiAnalytics);
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Translation timeout after 1 minute')), 60000)
            );

            const translated = await Promise.race([translationPromise, timeoutPromise]);

            setTamilAnalytics(translated);
            setAnalyticsLanguage('tamil');

            // Save to database
            try {
                const updatedCVData = {
                    ...(submissionData.cv_data || {}),
                    tamilAnalytics: translated,
                    tamilAnalyticsGeneratedAt: new Date().toISOString()
                };

                await WizardSubmissionService.updateCVAnalysis(submissionData.id, updatedCVData);
                console.log('✅ Tamil analytics saved to database');
            } catch (saveError) {
                console.error('⚠️ Failed to save Tamil analytics:', saveError);
            }

            return translated;
        } catch (error) {
            console.error('❌ Error translating analytics:', error);
            throw error;
        } finally {
            setTranslatingAnalytics(false);
        }
    }, [aiAnalytics, tamilAnalytics]);

    return {
        // State
        aiAnalytics,
        tamilAnalytics,
        analyticsLanguage,
        loadingAdvancedAnalytics,
        generatingAnalytics,
        translatingAnalytics,
        salesMetrics,
        competitorAnalysis,
        marketInsights,
        followUpTasks,
        riskFactors,
        opportunityScore,

        // Actions
        setAiAnalytics,
        setAnalyticsLanguage,
        setLoadingAdvancedAnalytics,
        setGeneratingAnalytics,
        setSalesMetrics,
        setCompetitorAnalysis,
        setMarketInsights,
        setFollowUpTasks,
        setRiskFactors,
        setOpportunityScore,
        loadTamilAnalytics,
        translateToTamil,

        // Utilities
        getCurrentAnalytics
    };
};

export default useAnalytics;
